/**
 * Asteroids engine for the hidden arcade (C). Pure wraparound vector-game logic.
 */
export const WIDTH = 224;
export const HEIGHT = 256;
export const READY_MS = 1000;
export const WAVE_CLEAR_MS = 1200;
export const SHIP_RADIUS = 6;
export const ROTATE_SPEED = 0.0045;
export const THRUST_ACCEL = 0.00018;
export const MAX_SPEED = 0.14;
export const BULLET_SPEED = 0.22;
export const BULLET_LIFE_MS = 1100;
export const FIRE_COOLDOWN_MS = 220;
export const INVULNERABLE_MS = 1800;
export const ASTEROID_RADII = { 3: 15, 2: 9, 1: 5 };
export const ASTEROID_POINTS = { 3: 20, 2: 50, 1: 100 };

export function wrap(value, max) {
    if (value < 0) {
        return value + max;
    }
    if (value >= max) {
        return value - max;
    }
    return value;
}

function distance(a, b) {
    const rawDx = Math.abs(a.x - b.x);
    const rawDy = Math.abs(a.y - b.y);
    const dx = Math.min(rawDx, WIDTH - rawDx);
    const dy = Math.min(rawDy, HEIGHT - rawDy);
    return Math.sqrt(dx * dx + dy * dy);
}

function randomAngle(rng) {
    return rng() * Math.PI * 2;
}

export function createAsteroid({ rng, x, y, size = 3, wave = 1, angle = null }) {
    const direction = angle === null ? randomAngle(rng) : angle;
    const speed = (0.018 + rng() * 0.018) * Math.min(1.8, 1 + (wave - 1) * 0.05);
    const points = [];
    for (let i = 0; i < 10; i += 1) {
        points.push(0.72 + rng() * 0.28);
    }
    return {
        x,
        y,
        vx: Math.cos(direction) * speed,
        vy: Math.sin(direction) * speed,
        size,
        radius: ASTEROID_RADII[size],
        rotation: randomAngle(rng),
        spin: (rng() - 0.5) * 0.001,
        points,
    };
}

function resetShip(state) {
    state.ship = {
        x: WIDTH / 2,
        y: HEIGHT / 2,
        vx: 0,
        vy: 0,
        angle: -Math.PI / 2,
        cooldownMs: 0,
        invulnerableMs: INVULNERABLE_MS,
    };
}

function spawnWaveAsteroids(state) {
    const count = state.wave + 3;
    state.asteroids = [];
    for (let i = 0; i < count; i += 1) {
        let x = state.rng() * WIDTH;
        let y = state.rng() * HEIGHT;
        let attempts = 0;
        while (distance({ x, y }, state.ship) < 55 && attempts < 12) {
            x = state.rng() * WIDTH;
            y = state.rng() * HEIGHT;
            attempts += 1;
        }
        if (distance({ x, y }, state.ship) < 55) {
            x = i % 2 === 0 ? 12 : WIDTH - 12;
            y = 20 + (i * 47) % (HEIGHT - 40);
        }
        state.asteroids.push(createAsteroid({
            rng: state.rng, x, y, size: 3, wave: state.wave,
        }));
    }
}

export function startWave(state, wave) {
    state.wave = wave;
    state.status = 'ready';
    state.statusMs = READY_MS;
    state.bullets = [];
    state.thrusting = false;
    resetShip(state);
    spawnWaveAsteroids(state);
    return state;
}

export function createGame({ rng = Math.random } = {}) {
    const state = {
        rng,
        wave: 1,
        score: 0,
        lives: 3,
        status: 'ready',
        statusMs: READY_MS,
        ship: null,
        bullets: [],
        asteroids: [],
        thrusting: false,
        elapsedMs: 0,
        events: [],
    };
    return startWave(state, 1);
}

function capVelocity(ship) {
    const speed = Math.sqrt(ship.vx * ship.vx + ship.vy * ship.vy);
    if (speed > MAX_SPEED) {
        ship.vx = (ship.vx / speed) * MAX_SPEED;
        ship.vy = (ship.vy / speed) * MAX_SPEED;
    }
}

function fire(state) {
    const { ship } = state;
    if (ship.cooldownMs > 0 || state.bullets.length >= 5) {
        return;
    }
    const dx = Math.cos(ship.angle);
    const dy = Math.sin(ship.angle);
    state.bullets.push({
        x: wrap(ship.x + dx * 8, WIDTH),
        y: wrap(ship.y + dy * 8, HEIGHT),
        vx: ship.vx + dx * BULLET_SPEED,
        vy: ship.vy + dy * BULLET_SPEED,
        lifeMs: BULLET_LIFE_MS,
    });
    ship.cooldownMs = FIRE_COOLDOWN_MS;
    state.events.push({ type: 'shoot' });
}

function splitAsteroid(state, asteroid) {
    if (asteroid.size <= 1) {
        return;
    }
    const childSize = asteroid.size - 1;
    const base = Math.atan2(asteroid.vy, asteroid.vx);
    [-0.65, 0.65].forEach((offset) => {
        const child = createAsteroid({
            rng: state.rng,
            x: asteroid.x,
            y: asteroid.y,
            size: childSize,
            wave: state.wave,
            angle: base + offset,
        });
        child.vx += asteroid.vx * 0.35;
        child.vy += asteroid.vy * 0.35;
        state.asteroids.push(child);
    });
}

function hitAsteroid(state, asteroidIndex) {
    const asteroid = state.asteroids.splice(asteroidIndex, 1)[0];
    state.score += ASTEROID_POINTS[asteroid.size];
    state.events.push({ type: 'asteroidHit', size: asteroid.size });
    splitAsteroid(state, asteroid);
}

function hitShip(state) {
    state.lives -= 1;
    state.events.push({ type: 'shipHit' });
    state.bullets = [];
    if (state.lives <= 0) {
        state.status = 'gameOver';
        state.thrusting = false;
        state.events.push({ type: 'gameOver' });
        return;
    }
    resetShip(state);
}

function updateShip(state, input, dtMs) {
    const { ship } = state;
    const turn = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    ship.angle += turn * ROTATE_SPEED * dtMs;
    state.thrusting = !!input.thrust;
    if (state.thrusting) {
        ship.vx += Math.cos(ship.angle) * THRUST_ACCEL * dtMs;
        ship.vy += Math.sin(ship.angle) * THRUST_ACCEL * dtMs;
        capVelocity(ship);
    }
    const drag = Math.pow(0.995, dtMs / (1000 / 60));
    ship.vx *= drag;
    ship.vy *= drag;
    ship.x = wrap(ship.x + ship.vx * dtMs, WIDTH);
    ship.y = wrap(ship.y + ship.vy * dtMs, HEIGHT);
    ship.cooldownMs = Math.max(0, ship.cooldownMs - dtMs);
    ship.invulnerableMs = Math.max(0, ship.invulnerableMs - dtMs);
    if (input.fire) {
        fire(state);
    }
}

function updateObjects(state, dtMs) {
    state.bullets.forEach((bullet) => {
        bullet.x = wrap(bullet.x + bullet.vx * dtMs, WIDTH);
        bullet.y = wrap(bullet.y + bullet.vy * dtMs, HEIGHT);
        bullet.lifeMs -= dtMs;
    });
    state.bullets = state.bullets.filter(bullet => bullet.lifeMs > 0);
    state.asteroids.forEach((asteroid) => {
        asteroid.x = wrap(asteroid.x + asteroid.vx * dtMs, WIDTH);
        asteroid.y = wrap(asteroid.y + asteroid.vy * dtMs, HEIGHT);
        asteroid.rotation += asteroid.spin * dtMs;
    });
}

function checkBulletHits(state) {
    for (let bulletIndex = state.bullets.length - 1; bulletIndex >= 0; bulletIndex -= 1) {
        const bullet = state.bullets[bulletIndex];
        const asteroidIndex = state.asteroids.findIndex(asteroid => distance(bullet, asteroid) <= asteroid.radius);
        if (asteroidIndex >= 0) {
            state.bullets.splice(bulletIndex, 1);
            hitAsteroid(state, asteroidIndex);
        }
    }
}

function checkShipHit(state) {
    if (state.ship.invulnerableMs > 0) {
        return;
    }
    const collided = state.asteroids.some(asteroid => distance(state.ship, asteroid) < SHIP_RADIUS + asteroid.radius);
    if (collided) {
        hitShip(state);
    }
}

export function step(state, input = {}, dtMs = 1000 / 60) {
    if (state.status === 'gameOver') {
        return state;
    }
    if (state.status === 'ready') {
        state.statusMs -= dtMs;
        if (state.statusMs <= 0) {
            state.status = 'playing';
            state.statusMs = 0;
            state.events.push({ type: 'start' });
        }
        return state;
    }
    if (state.status === 'waveCleared') {
        state.statusMs -= dtMs;
        if (state.statusMs <= 0) {
            startWave(state, state.wave + 1);
        }
        return state;
    }

    state.elapsedMs += dtMs;
    updateShip(state, input, dtMs);
    updateObjects(state, dtMs);
    checkBulletHits(state);
    checkShipHit(state);
    if (state.status === 'playing' && state.asteroids.length === 0) {
        state.status = 'waveCleared';
        state.statusMs = WAVE_CLEAR_MS;
        state.thrusting = false;
        state.events.push({ type: 'waveCleared', wave: state.wave });
    }
    return state;
}
